import { Fragment, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import html2pdf from "html2pdf.js";
import { IoCheckmark } from "react-icons/io5";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiCheckboxCircleLine, RiErrorWarningLine } from "react-icons/ri";

import api from "../../../services/api";
import Modal from "../../../components/modal";
import SendEmail from "./send";
import Switch from "../../../components/Switch";
import Avoir from "./components/Avoir";
import Loader from "@/components/loader";
import { validateInvoice } from "../utils";
import InvoicePdf from "./invoicePdf";

const Editor = ({ invoice, setInvoice, project }) => {
  const [organisation, setOrganisation] = useState(null);
  const pdfRef = useRef(null);

  useEffect(() => {
    if (invoice?.organisation_id || project?.organisation_id) {
      api.get(`/organisation/${invoice.organisation_id || project.organisation_id}`).then(({ data }) => setOrganisation(data));
    }
  }, [invoice?.organisation_id, project?.organisation_id]);

  const handleInvoiceChange = async (values) => {
    try {
      const res = await api.put(`/invoice/${invoice._id}`, values);
      if (!res.ok) throw new Error("Error while updating invoice");
      setInvoice(res.data);
      toast.success("Invoice updated");
    } catch (error) {
      console.error(error);
      toast.error("Error while updating invoice");
    }
  };

  if (!project || !invoice) return <div>Loading</div>;

  return (
    <div className="h-full flex">
      <div className="overflow-scroll h-full flex-1">
        <Content invoice={invoice} setInvoice={setInvoice} project={project} onChange={handleInvoiceChange} />
      </div>
      <div className="w-[25%] h-full">
        <Menu invoice={invoice} setInvoice={setInvoice} project={project} organisation={organisation} pdfRef={pdfRef} onChange={handleInvoiceChange} />
      </div>
      {/* Hidden div for PDF generation */}
      <div style={{ position: "absolute", left: "-9999px", top: 0 }}>
        <InvoicePdf ref={pdfRef} invoice={invoice} project={project} organisation={organisation} />
      </div>
    </div>
  );
};

const Menu = ({ invoice, setInvoice, project, organisation, pdfRef, onChange }) => {
  const [downloading, setDownloading] = useState(false);
  const [discount, setDiscount] = useState(invoice.discount);
  const [total, setTotal] = useState(invoice.subTotal - (invoice.subTotal - (invoice.subTotal * (100 - invoice.discount)) / 100));

  useEffect(() => {
    if (invoice && (!invoice.itemsInvoice || invoice.itemsInvoice.length === 0) && invoice.sent !== "yes") {
      handleLoad();
    }
  }, []);

  const handleSave = async () => {
    await onChange(invoice);
  };

  const handlePrint = async () => {
    if (!organisation) {
      toast.error("Organisation not loaded yet");
      return;
    }
    setDownloading(true);
    try {
      const currentInvoice = invoice;
      if (!currentInvoice.index) throw new Error("Missing invoice number — fill in the Reference field");

      // Generate PDF client-side
      const element = pdfRef.current;
      if (!element) throw new Error("PDF template not ready");

      const pdfBlob = await html2pdf()
        .set({
          margin: 0,
          filename: `invoice-${currentInvoice.index}.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, width: 794, windowWidth: 794 },
          jsPDF: { unit: "px", format: [794, 1122], hotfixes: ["px_scaling"] },
          pagebreak: { mode: ["css"] },
        })
        .from(element)
        .outputPdf("blob");

      // Convert to base64 and upload via existing endpoint
      const reader = new FileReader();
      const base64 = await new Promise((resolve) => {
        reader.onloadend = () => resolve(reader.result.split(",")[1]);
        reader.readAsDataURL(pdfBlob);
      });

      const { data, ok } = await api.post(`/invoice/generate/${currentInvoice._id}`, { pdf: base64 });
      if (!ok) throw new Error("Failed to upload PDF");
      setInvoice(data);
      toast.success("PDF generated");

      // Download the PDF locally
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${currentInvoice.index}.pdf`;
      a.click();
      URL.revokeObjectURL(url);

      // Also open the uploaded file
      if (data.file) window.open(data.file, "_blank");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error(error.message || "Failed to generate PDF");
    }
    setDownloading(false);
  };

  const handleLoad = async () => {
    if (invoice.sent === "yes") {
      toast.error("Invoice already sent");
      return;
    }
    try {
      const { data: dataInvoice, error } = await api.get(`/invoice/load_editor/${invoice._id}`);
      if (error) {
        toast.error(error);
        return;
      }

      setInvoice(dataInvoice);
      toast.success("Invoice loaded");
    } catch (e) {
      console.log("Error in handleLoad:", e);
      toast.error("An error occurred while loading the invoice");
    }
  };

  return (
    <div className="space-y-4 pl-8 bg-white">
      <div className="flex-1 space-y-2">
        <CheckInconsistency invoice={invoice} project={project} />
        <button className="blue-btn w-full" onClick={handleLoad}>
          Load the invoice
        </button>

        <div className="w-full flex items-center relative group gap-2">
          <button onClick={handlePrint} className="blue-btn w-full" disabled={downloading}>
            {downloading ? "Generating..." : "Generate PDF"}
          </button>

          {invoice.file && (
            <a href={invoice.file} target="_blank" rel="noreferrer" className="blue-btn w-10 text-center">
              <MdOutlineRemoveRedEye className="text-lg font-bold" />
            </a>
          )}
        </div>
        <SendEmail
          invoice={invoice}
          project={project}
          onPrint={() => null}
          onDone={() => {
            setInvoice({ ...invoice, sent: "yes", freezed: "yes" });
          }}
        />
        <Avoir invoice={invoice} />
        <button className="empty-btn w-full" onClick={handleSave}>
          Save invoice
        </button>
      </div>

      <div className="grid grid-cols-2 gap-y-4 gap-x-2 items-center">
        <label className="text-sm font-semibold">Freezed</label>

        <Switch checked={invoice.freezed === "yes"} onChange={(e) => onChange({ ...invoice, freezed: e ? "yes" : "no" })} />

        <label className="text-sm font-semibold">Sent</label>

        <Switch
          checked={invoice.sent === "yes"}
          onChange={(e) => {
            if (e) {
              const sentAt = new Date();
              const dueAt = new Date(sentAt);
              dueAt.setDate(dueAt.getDate() + 30);
              onChange({ ...invoice, sent: "yes", sentAt, dueAt: dueAt.toISOString(), freezed: "yes" });
            } else {
              onChange({ ...invoice, sent: "no", sentAt: null, freezed: invoice.freezed });
            }
          }}
        />

        <label className="text-sm font-semibold">Sent at</label>
        <input
          disabled={invoice.sent === "yes"}
          className="input p-1 flex-1"
          type="date"
          value={invoice.sentAt ? new Date(invoice.sentAt).toISOString().slice(0, 10) : ""}
          onChange={(e) => onChange({ ...invoice, sent: "yes", sentAt: e.target.value })}
        />

        <label className="text-sm font-semibold">Received</label>

        <Switch checked={invoice.received === "yes"} onChange={(e) => onChange({ ...invoice, received: e ? "yes" : "no" })} />

        <label className="text-sm font-semibold">Received at</label>
        <input
          disabled={invoice.received === "yes"}
          className="input p-1 flex-1"
          type="date"
          value={invoice.receivedAt ? new Date(invoice.receivedAt).toISOString().slice(0, 10) : ""}
          onChange={(e) => onChange({ ...invoice, received: "yes", receivedAt: e.target.value })}
        />

        <label className="text-sm font-semibold">Discount %</label>

        <div className="flex gap-2 items-center">
          {discount !== invoice.discount ? (
            <button onClick={() => onChange({ discount })} className="blue-btn p-1 h-6">
              <IoCheckmark />
            </button>
          ) : null}
          <input type="number" className="input p-1 text-right" value={discount} onChange={(e) => setDiscount(e.target.value)} />
        </div>

        <label className="text-sm font-semibold">Calculate discount</label>
        <div className="flex gap-2 items-center">
          {total !== invoice.subTotal - (invoice.subTotal - (invoice.subTotal * (100 - invoice.discount)) / 100) && total > 0 ? (
            <button onClick={() => onChange({ discount: 100 - (total * 100) / invoice.subTotal })} className="blue-btn p-1 h-6">
              <IoCheckmark />
            </button>
          ) : null}
          <input type="number" className="input p-1 text-right" value={total} onChange={(e) => setTotal(e.target.value)} />
        </div>
        <Penalties invoice={invoice} setInvoice={setInvoice} />
      </div>
    </div>
  );
};

const Content = ({ invoice, setInvoice, project, onChange }) => {
  const today = new Date();

  const updateInvoiceDates = async () => {
    const dueAt = new Date(today);
    dueAt.setDate(today.getDate() + 30);
    try {
      const { data, ok } = await api.put(`/invoice/${invoice._id}`, {
        sentAt: today.toISOString(),
        dueAt: dueAt.toISOString(),
      });
      if (!ok) {
        throw new Error("Failed to update invoice with dates");
      }
      setInvoice(data);
      toast.success("Invoice dates initialized");
    } catch (error) {
      console.error("Error updating invoice:", error);
      toast.error("Failed to initialize invoice dates");
    }
  };

  useEffect(() => {
    if (invoice.sent !== "yes" && (!invoice.sentAt || new Date(invoice.sentAt) < today)) {
      updateInvoiceDates();
    }
  }, []);

  return (
    <div className="flex justify-center py-4 bg-gray-800">
      <div className="col-span-3 grid grid-cols-2 gap-4 p-4 bg-white rounded border w-[794px]">
        <div>
          <Seller organizationId={invoice.organisation_id ?? project.organisation_id} />
        </div>
        <div>
          <Customer invoice={invoice} project={project} setInvoice={setInvoice} onChange={onChange} />
        </div>
        <div className="col-span-2">
          <InvoiceDetails values={invoice} onChange={setInvoice} onSave={onChange} />
        </div>
        <div className="col-span-2">
          <Table values={invoice} project={project} onChange={setInvoice} />
        </div>
        <div className="col-span-2">
          <TermsAndConditions values={invoice} onChange={setInvoice} />
        </div>
      </div>
    </div>
  );
};

const CheckInconsistency = ({ invoice, project }) => {
  const errors = validateInvoice(invoice, project);

  if (errors?.length) {
    return (
      <div className="border-l-4 border-red-400 bg-red-50 p-4 mb-2">
        <div className="flex">
          <div className="flex-shrink-0">
            <RiErrorWarningLine className="h-5 w-5 text-red-400" aria-hidden="true" />
          </div>
          <div className="ml-3">
            {errors.map((text) => (
              <p className="text-sm text-red-700">{text}</p>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-3 border-l-4 border-green-400 bg-green-100 text-green-500 p-4 mb-2">
      <RiCheckboxCircleLine className="h-5 w-5" />
      <div>
        <p className="text-sm font-semibold">No issue detected</p>
        <p className="text-sm">{invoice.sent === "yes" ? "Invoice sent." : "Ready to send!"}</p>
      </div>
    </div>
  );
};

const InvoiceDetails = ({ values, onChange, onSave }) => {
  return (
    <div className="flex-1 grid grid-cols-4 items-center gap-2">
      <label className="text-sm font-semibold text-right">Name</label>
      <input className="input p-2" value={values.name} onChange={(v) => onChange({ ...values, name: v.target.value })} />

      <label className="text-sm font-semibold text-right">Sent date</label>

      <input
        disabled={values.sent === "yes"}
        type="date"
        className="input"
        value={values.sentAt ? new Date(values.sentAt).toISOString().slice(0, 10) : ""}
        onChange={(e) => {
          const sentDate = e.target.value;
          if (sentDate) {
            const date = new Date(sentDate);
            date.setDate(date.getDate() + 30);
            const dueDate = date.toISOString().slice(0, 10);
            onChange({ ...values, sentAt: sentDate, dueAt: dueDate });
          } else {
            onChange({ ...values, sentAt: null, dueAt: null });
          }
        }}
      />

      <label className="text-sm font-semibold text-right">Reference</label>
      {/* Saved on blur, not on "Save invoice": the PDF carries this number, so it has to be
          persisted before it can be generated. */}
      <input
        disabled={values.sent === "yes"}
        className="input p-2"
        placeholder="Qonto invoice number"
        value={values.index || ""}
        onChange={(e) => onChange({ ...values, index: e.target.value })}
        onBlur={(e) => onSave({ ...values, index: e.target.value })}
      />

      <label className="text-sm font-semibold text-right">Due date</label>
      <input
        disabled={values.sent === "yes"}
        type="date"
        className="input"
        value={values.dueAt ? new Date(values.dueAt).toISOString().slice(0, 10) : ""}
        onChange={(e) => onChange({ ...values, dueAt: e.target.value })}
      />
    </div>
  );
};

const Seller = ({ organizationId }) => {
  const [loading, setLoading] = useState(true);
  const [organisation, setOrganisation] = useState(null);

  const fetchOrganisationData = async (organisationId) => {
    try {
      setLoading(true);
      const { ok, data } = await api.get(`/organisation/${organisationId}`);
      if (!ok) throw Error("Impossible de récupérer l'organisation. Merci de remplir l'organisation du projet (project > edit)");
      setOrganisation(data);
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Une erreur est survenue lors du chargement de l'organisation. Merci de remplir l'organisation du projet");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganisationData(organizationId);
  }, [organizationId]);

  if (loading) return <Loader />;

  return (
    <div className="w-full flex flex-col">
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">{organisation?.name}</h2>
        <p>{organisation?.address}</p>
        <p>
          <span className="font-semibold mr-2">Email:</span> {organisation?.email}
          <br />
          <span className="font-semibold mr-2">VAT:</span> {organisation?.vat} <br />
          <span className="font-semibold mr-2">IBAN:</span> {organisation?.iban} <br />
        </p>
      </div>
    </div>
  );
};

const Customer = ({ invoice, project, setInvoice, onChange }) => {
  return (
    <div className="flex-1 grid grid-cols-4 items-center gap-2">
      <label className="text-sm text-gray-950 font-semibold text-right">Name</label>
      <div className="col-span-3">
        <input
          value={invoice.customer_name || project.clientName}
          onChange={(e) => setInvoice({ ...invoice, customer_name: e.target.value })}
          onBlur={(e) => onChange({ ...invoice, customer_name: e.target.value })}
          className="input p-2"
        />
      </div>
      <label className="text-sm text-gray-950 font-semibold text-right">Address</label>
      <div className="col-span-3">
        <textarea
          rows={2}
          value={invoice.customer_address || project.clientAddress}
          onChange={(e) => setInvoice({ ...invoice, customer_address: e.target.value })}
          onBlur={(e) => onChange({ ...invoice, customer_address: e.target.value })}
          className="input p-2"
        />
      </div>
      <label className="text-sm text-gray-950 font-semibold text-right">Email</label>
      <div className="col-span-3">
        <p className="font-semibold p-2">{(invoice.customer_emails || project.clientMails || []).join(", ")}</p>
      </div>
      <label className="text-sm text-gray-950 font-semibold text-right">VAT</label>
      <div className="col-span-3">
        <input
          value={invoice.customer_vat || project.clientVat}
          onChange={(e) => setInvoice({ ...invoice, customer_vat: e.target.value })}
          onBlur={(e) => onChange({ ...invoice, customer_vat: e.target.value })}
          className="input p-2"
        />
      </div>
      <label className="text-sm text-gray-950 font-semibold text-right">SIREN</label>
      <div className="col-span-3">
        <input
          value={invoice.customer_siren || project.clientSiret}
          onChange={(e) => setInvoice({ ...invoice, customer_siren: e.target.value })}
          onBlur={(e) => onChange({ ...invoice, customer_siren: e.target.value })}
          className="input p-2"
        />
      </div>
    </div>
  );
};

const Table = ({ values, project, onChange }) => {
  const handleItemChange = async (item, index) => {
    const newValues = { ...values };
    newValues.itemsInvoice[index] = item;
    try {
      const res = await api.put(`/invoice/${newValues._id}`, newValues);
      if (res.ok) {
        onChange(res.data);
        toast.success("Invoice updated");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to update invoice");
    }
  };
  const handleAddItem = async () => {
    const newValues = { ...values };
    newValues.itemsInvoice.push({ name: "", description: "", price: 0, quantity: 1, unit: "unit", totalPrice: 0 });
    try {
      const res = await api.put(`/invoice/${newValues._id}`, newValues);
      if (res.ok) {
        onChange(res.data);
        toast.success("Invoice updated");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to update invoice");
    }
  };

  const handleItemDelete = async (index) => {
    const newValues = { ...values };
    newValues.itemsInvoice.splice(index, 1);
    try {
      const res = await api.put(`/invoice/${newValues._id}`, newValues);
      if (res.ok) {
        onChange(res.data);
        toast.success("Invoice updated");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to update invoice");
    }
  };

  const discountAmount = values.subTotal - (values.subTotal * (100 - values.discount)) / 100;
  const unTaxedAmount = values.subTotal - (values.subTotal - (values.subTotal * (100 - values.discount)) / 100);

  const rawTaxType = project?.taxType || values.taxType || "";
  const match = rawTaxType.match(/(\d+(?:[.,]\d+)?)%/);
  const rate = match ? parseFloat(match[1].replace(",", ".")) : null;
  const isZeroRate = rate === 0;
  const isNotApplicable = rawTaxType === "N/A (Not applicable)" || rawTaxType === "";
  const showReverseCharge = isZeroRate || isNotApplicable;
  const taxTypeLabel = values.taxType || project?.taxType || "(not applied)";

  return (
    <table className="table-fixed text-sm">
      <thead className="border-b border-gray-300">
        <tr>
          <th colSpan={2} className="text-left p-2 pl-10">
            Name
          </th>
          <th colSpan={2} className="text-left p-2">
            Description
          </th>
          <th className="text-right p-2">Price</th>
          <th className="text-right p-2">Quantity</th>
          <th className="text-right p-2">Total</th>
        </tr>
      </thead>
      <tbody>
        {values.itemsInvoice.length > 0 ? (
          values.itemsInvoice.map((item, i) => <Item key={i} item={item} onChange={(item) => handleItemChange(item, i)} onDelete={() => handleItemDelete(i)} />)
        ) : (
          <tr>
            <td colSpan={7} className="p-2 text-sm text-center text-gray-400">
              No data linked to the invoice
            </td>
          </tr>
        )}
        <tr>
          <td colSpan={7} className="p-2 text-sm text-center text-gray-400">
            <div className="flex justify-end">
              <button className="gray-btn" onClick={handleAddItem}>
                +
              </button>
            </div>
          </td>
        </tr>
      </tbody>
      <tfoot className="font-semibold">
        <tr>
          <td colSpan={6} className="p-2 text-right">
            Sub total
          </td>
          <td className="text-right text-sm">{values.subTotal.toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</td>
        </tr>
        {discountAmount > 0 && (
          <tr>
            <td colSpan={6} className="p-2 text-right">
              Discount €
            </td>
            <td className="text-right">{`-${discountAmount.toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}`}</td>
          </tr>
        )}
        <tr>
          <td colSpan={6} className="p-2 text-right">
            Untaxed total
          </td>
          <td className="text-right text-sm">{unTaxedAmount.toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</td>
        </tr>
        <tr>
          <td colSpan={6} className="p-2">
            <div className={"flex " + (showReverseCharge ? "justify-between" : "justify-end") + " items-center"}>
              {showReverseCharge && (
                <div>
                  <span className="text-sm italic text-gray-600 font-normal">VAT reverse charge – Article 194 of Directive 2006/112/EC</span>
                  <br />
                </div>
              )}
              <span className="mt-1 inline-block">Tax {taxTypeLabel}</span>
            </div>
          </td>
          <td className="text-right text-sm">{(values.taxValue || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</td>
        </tr>
        <tr>
          <td colSpan={6} className="p-2 text-right">
            Total due
          </td>
          <td className="text-right text-sm">{(values.total || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</td>
        </tr>
      </tfoot>
    </table>
  );
};

const Item = ({ item, onChange, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <>
      <EditItemModal item={item} onChange={onChange} onDelete={onDelete} isOpen={isOpen} onClose={() => setIsOpen(false)} />
      <tr className="cursor-pointer hover:bg-gray-100 rounded-lg" onClick={() => setIsOpen(true)}>
        <td colSpan={2} className="p-2 text-sm">
          <p>{item.name}</p>
        </td>
        <td colSpan={2} className="p-2 text-sm">
          {item.description?.split("\n").map((line, index) => (
            <Fragment key={index}>
              {line}
              <br />
            </Fragment>
          ))}
        </td>
        <td className="p-2 text-right">{(item.price || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</td>
        <td className="p-2 text-right">
          {item.quantity} {item.unit}
        </td>
        <td className="p-2 text-right">{(item.totalPrice || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</td>
      </tr>
    </>
  );
};

const EditItemModal = ({ item, onChange, onDelete, isOpen, onClose }) => {
  const [values, setValues] = useState({ ...item });
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    await onChange({ ...values, totalPrice: values.price * values.quantity });
    onClose();
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    await onDelete();
    onClose();
    setLoading(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="w-1/2">
      <div className="p-6">
        <h2 className="text-2xl">Edit item</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-gray-950 font-semibold">Name</label>
            <input type="text" className="input" value={values.name} onChange={(e) => setValues({ ...values, name: e.target.value })} />
          </div>
          <div>
            <label className="text-sm text-gray-950 font-semibold">Description</label>
            <textarea className="input" value={values.description} onChange={(e) => setValues({ ...values, description: e.target.value })} />
          </div>
          <div>
            <label className="text-sm text-gray-950 font-semibold">Price</label>
            <input type="number" className="input" value={values.price} onChange={(e) => setValues({ ...values, price: parseFloat(e.target.value) })} />
          </div>
          <div>
            <label className="text-sm text-gray-950 font-semibold">Quantity</label>
            <input type="number" className="input" value={values.quantity} onChange={(e) => setValues({ ...values, quantity: parseFloat(e.target.value) })} />
          </div>
          <div>
            <label className="text-sm text-gray-950 font-semibold">Unit</label>
            <select className="input" value={values.unit} onChange={(e) => setValues({ ...values, unit: e.target.value })}>
              <option value=""></option>
              <option value="unit">Unit</option>
              <option value="days">Days</option>
              <option value="month">Month</option>
            </select>
          </div>
        </div>

        <div className="flex justify-between mt-4">
          <div>
            <button onClick={handleDelete} className="red-btn" disabled={loading}>
              {loading ? "Loading... " : "Delete"}
            </button>
          </div>

          <div className="flex justify-end mt-4">
            <button onClick={onClose} className="empty-btn">
              {loading ? "Loading... " : "Cancel"}
            </button>
            <button onClick={handleSave} className="blue-btn ml-2" disabled={loading}>
              {loading ? "Loading... " : "Save"}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const Penalties = ({ invoice, setInvoice }) => {
  if (invoice.dueAt && new Date(invoice.dueAt) < new Date()) {
    const dayslate = Math.floor((new Date() - new Date(invoice.dueAt)) / (1000 * 60 * 60 * 24));
    const cal = Math.ceil(invoice.total * 0.15 * (dayslate / 365) + 40);
    return (
      <div className="flex items-center mt-2">
        <div className="text-gray-800 font-medium">{`Penalties (${dayslate} days)`}</div>
        <div className="ml-2 text-gray-600">{`${cal.toFixed(2)}€`}</div>
        {/* <button
          className="ml-3 ms-auto items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          onClick={async () => {
            let items = invoice.itemsInvoice;
            items = items.filter((item) => item.name !== "Penalties");
            items = [...items, { name: "Penalties", description: `${dayslate} days late`, quantity: 1, price: cal, totalPrice: cal }];

            console.log("items", items);
            const { data } = await api.put(`/invoice/${invoice._id}`, { itemsInvoice: items });
            setInvoice(data);

            toast.success("Invoice updated");
          }}>
          Apply
        </button> */}
      </div>
    );
  }
  return <div />;
};

const TermsAndConditions = ({ values, onChange }) => {
  const value =
    values.termsAndConditions ||
    "Please pay within 30 days. In the event of late payment, a penalty of 15% interest rate will be applied, to which will be added a lump sum compensation for recovery costs of €40.";

  return (
    <div className="space-y-2">
      <h2 className="text-lg">Terms & conditions</h2>
      <textarea rows={4} className="input p-2" value={values.termsAndConditions} onChange={(v) => onChange({ ...values, termsAndConditions: v.target.value })} />
    </div>
  );
};

export default Editor;
